import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Box, Grid, Stack, Tooltip, Typography, styled, Link } from "@mui/material";
import Image from "next/image";
import { useRouter } from "next/router";
import React, { useContext } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";

import { MemoizedButton } from "../../SDK/button";
import {
  imagesList,
  offer_id,
  offer_name,
  tag_id,
  tag_name,
} from "../../constants";
import { primary } from "../../constants/theme";
import { Context } from "../../context/context";
import { sendAnalytics } from "../../utils/analyticsCommon";
import { createCookieInHour } from "../../utils/cookie";

const CustomerVehiclesCard = styled(Box)(({ theme }) => ({
  boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.15)",
  borderRadius: "0.5em",
  paddingTop: "0px !important",
  margin: "4em",
  background: "white",
  ".image-section": {
    objectFit: "contain",
    width: "100% !important",
    position: "relative !important",
  },
  ".vehicle-data-brand": {
    fontWeight: "600",
    fontSize: "36px",
    lineHeight: "150%",
    color: primary.dark,
    textTransform: "Capitalize",
  },
  ".vehicle-data-name": {
    fontWeight: "600",
    fontSize: "22px",
    lineHeight: "34px",
    color: "#BDBDBD",
    textTransform: "Capitalize",
  },
  ".details-box": {
    textAlign: "center",
  },
  ".details-box .details-grid-item": {
    borderRight: `2px solid ${primary.main}`,

    paddingTop: "4px",
    "&:last-child": {
      borderRight: "unset",
    },
  },
  ".details-head": {
    textAlign: "left",
    fontWeight: "400",
    fontSize: "1em",
    lineHeight: "1.5em",
    color: "#000000",
  },
  ".details-desc": {
    textAlign: "left",
    fontWeight: "700",
    fontSize: "1.5em",
    lineHeight: "1.5em",
    color: "#000000",
  },
  ".start-price": {
    color: primary.main,
  },

  ".whatsAppIcon": {
    padding: "0px",
    width: "4em",
    height: "4em",

    border: "2px solid #f5f5f5",
    borderRadius: "50%",
    color: primary.white,
    textAlign: "center",

    background: primary.mainGreen,
    boxShadow: "none",
    svg: {
      width: "1.5em",
      height: "1.5em",
    },
  },
  ".guaranteed-image": {
    textAlign: "end",
    position: "absolute",
    right: "10px",
  },
  ".vehicle-image": {
    paddingTop: "0px !important",
  },
  ".MuiGrid-item": {
    paddingTop: "0px !important",
  },
  ".vehicle-image-box": {
    minHeight: "400px",
  },
  [theme.breakpoints.down("md")]: {
    margin: "0.5em",
    marginTop: "2em",
    marginBottom: "2em",
    ".MuiGrid-item": {
      paddingTop: "0px !important",
    },
    ".guaranteed-image": {
      position: "relative",
      width: "63%",
    },
    ".parentBox": {
      margin: "0em",
    },
    ".details-head": {
      fontSize: "0.7em",
    },
    ".details-desc": {
      fontSize: "1em",
    },
    "span.lazy-load-image-background.opacity.lazy-load-image-loaded": {
      textAlign: "end",
    },
    ".vehicle-data-brand": {
      fontSize: "24px",
      lineHeight: "30px",
    },
    ".vehicle-data-name": {
      fontSize: "18px",
      lineHeight: "24px",
    },
    ".whatsAppIcon": {
      borderRadius: "50%",
    },
    ".vehicle-image-box": {
      minHeight: "320px",
    },
  },
  ".button-link": {
    textDecoration: "none !important",
    color: "inherit !important",
    display: "block",
    width: "100%",
    
    "&:hover": {
      textDecoration: "none !important",
      color: "inherit !important",
    },
    
    "&:visited": {
      textDecoration: "none !important",
      color: "inherit !important",
    },
    
    "&:active": {
      textDecoration: "none !important",
      color: "inherit !important",
    },
    
    "&:focus": {
      textDecoration: "none !important",
      color: "inherit !important",
      outline: "none",
    },
    
    "&:link": {
      textDecoration: "none !important",
      color: "inherit !important",
    },
  },
}));

const VehicleCard = ({ vehicleData, index }) => {
  const state = useContext(Context);
  const { showStatePopup, showWhatsappPopup, changeVehicleBrand } =
    useContext(Context);

  const navigate = useRouter();
  console.log("navigate", vehicleData);

  const getNavigationUrl = () => {
    return `/electric-commercial-vehicles/${vehicleData.brand}/${vehicleData.vehicle_path_id}`;
  };

  const handleButtonClick = (e) => {
    e.preventDefault();
    let payload = {
      vehicle_name: vehicleData.name,
      vehicle_id: vehicleData.vehicle_id,
      booking_price_shown: vehicleData.price,
      emi_shown: vehicleData.emiPrice,
      offer_name: offer_name,
      offer_id: offer_id,
      tag_name: tag_name,
      tag_id: tag_id,
      button_name: "view vehicle",
    };

    sendAnalytics("book_now_button_clicked", payload, "en");

    changeVehicleBrand(vehicleData.brand);
    if (state?.selectedCity === "India") {
      showStatePopup("true");
    } else {
      navigate.push(getNavigationUrl());
    }
  };
  const pathname = navigate.asPath.split("?")[0];

  return (
    <CustomerVehiclesCard>
      <Box
        key={index}
        style={{ background: index % 2 === 1 ? "#FFEDF199" : "#FEF8E6" }}
      >
        <Grid container spacing={2}>
          <Grid
            className="vehicle-image"
            item
            xs={12}
            md={6}
            style={{
              paddingTop: "0px !important",
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", md: "80%" },
                paddingLeft: "2px",
              }}
              className="vehicle-image-box"
            >
              <Image
                src={vehicleData.vehiclePageImage}
                alt={vehicleData?.name}
                layout="fill"
                className={"image-section"}
              />
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            style={{ position: "relative", paddingTop: "0px !important" }}
          >
            <Box
              sx={{
                width: { xs: "90%", md: "70%" },
                p: { xs: "1em", md: "2em" },
                pt: { xs: "1em", md: "2em" },
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Stack>
                  <Typography variant="h2" className="vehicle-data-brand">
                    {vehicleData?.brand}{" "}
                    {/* </Typography>
                  <Typography className="vehicle-data-name"> */}
                    {vehicleData?.vehicle_name}
                  </Typography>
                </Stack>
                <Stack>
                  <LazyLoadImage
                    src={imagesList.vehicleGuaranteedBuyBack}
                    alt="Turno-3 years buyback  gaurantee"
                    effect="opacity"
                    className="guaranteed-image"
                  />
                </Stack>
              </Box>

              <Stack
                sx={{
                  pt: { xs: "0.5em", md: "1em" },
                  pb: { xs: "1em", md: "2em" },
                }}
              >
                <Grid
                  container
                  spacing={2}
                  className="details-box"
                  sx={{ mt: { xs: "1em", md: "2em" } }}
                >
                  <Grid
                    className="details-grid-item"
                    sx={{ display: "block", alignItems: "center" }}
                    item
                    xs={4}
                    md={4}
                  >
                    <Typography className="details-head">
                      Load capacity
                    </Typography>
                    <Typography className="details-desc">
                      {vehicleData?.load}
                    </Typography>
                  </Grid>
                  <Grid
                    className="details-grid-item"
                    sx={{ display: "block", alignItems: "center" }}
                    item
                    xs={4}
                    md={4}
                  >
                    <Typography className="details-head">
                      Battery Range
                    </Typography>
                    <Typography className="details-desc">
                      {vehicleData?.range}
                    </Typography>
                  </Grid>
                  <Grid
                    className="details-grid-item"
                    sx={{ display: "block", alignItems: "center" }}
                    item
                    xs={4}
                    md={4}
                  >
                    <Typography className="details-head">
                      Charging Time
                    </Typography>
                    <Typography className="details-desc">
                      {vehicleData?.chargingtime}
                    </Typography>
                  </Grid>
                </Grid>
              </Stack>

              <Stack>
                <Typography className="details-head start-price">
                  Starting Price
                </Typography>
                <Stack direction={"row"} spacing={0.5}>
                  <Typography
                    className="details-desc"
                    sx={{ fontSize: "1.4em !important" }}
                  >
                    ₹{vehicleData?.emiPrice}
                    p.m.
                  </Typography>
                  <Typography>
                    <Tooltip
                      sx={{ verticalAlign: "middle" }}
                      title="Prices are representable, calculated at a 36 month tenure"
                      placement="bottom-end"
                    >
                      <HelpOutlineIcon
                        style={{ fontWeight: "200" }}
                        fontSize="small"
                      />
                    </Tooltip>
                  </Typography>
                </Stack>
              </Stack>

              <Grid
                container
                direction="row"
                spacing={2}
                sx={{ mt: { xs: "10px", md: "50px", lg: "100px" } }}
              >
                <Grid item xs={10}>
                  <Link
                  href={getNavigationUrl()}
                  className="button-link"
                  onClick={handleButtonClick}
                >
                  <MemoizedButton
                    content={"View Details"}
                    sx={{ minWidth: "100%" }}
                  />
                </Link>
                </Grid>
                <Grid item xs={2} sx={{ paddingLeft: "8px !important " }}>
                  <MemoizedButton
                    className="whatsAppIcon"
                    content={<WhatsAppIcon sx={{ display: "inline" }} />}
                    handleClick={() => {
                      changeVehicleBrand(vehicleData.brand);
                      createCookieInHour(
                        "lead_trigger",
                        "whatsapp_chat_vehicle_card",
                        10
                      );
                      let payload = {
                        vehicle_name: vehicleData?.vehicle_name,
                        vehicle_id: vehicleData?.vehicle_id,
                        price_shown: vehicleData.price,
                        emi_shown: vehicleData.emiPrice,
                        offer_name: offer_name,
                        offer_id: offer_id,
                        tag_name: tag_name,
                        tag_id: tag_id,
                        connect_type: "vehicle_card",
                      };
                      sendAnalytics(
                        "whatsapp_connect_button_clicked",
                        payload,
                        "en"
                      );

                      showWhatsappPopup("vehicle_card");
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </CustomerVehiclesCard>
  );
};

const MemoizedVehicleCard = React.memo(VehicleCard);
export default MemoizedVehicleCard;
